import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { BADGES, BadgeDefinition } from '@/lib/badges';
import { calcStreak } from '@/lib/streak';
import { supabase } from '@/integrations/supabase/client';

export interface UnlockedBadge extends BadgeDefinition {
  currentValue: number;
  unlocked: boolean;
  progress: number; // 0-100
}

export function useBadges() {
  const { sessoes, disciplinas, revisoes, simulados } = useApp();
  const [diaDescanso, setDiaDescanso] = useState<number>(5); // default Saturday

  // Load user's rest day so streak badges align with the dashboard card
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const { data } = await supabase
        .from('profiles')
        .select('dia_descanso')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (!cancelled && data?.dia_descanso !== undefined && data?.dia_descanso !== null) {
        setDiaDescanso(Number(data.dia_descanso));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => {
    const { current, longest } = calcStreak(sessoes, diaDescanso);
    const bestStreak = Math.max(current, longest);

    const totalSessions = sessoes.length;
    const totalHours = sessoes.reduce((acc, s) => acc + s.tempoMin, 0) / 60;
    const totalQuestions = sessoes.reduce(
      (acc, s) => acc + (s.questoesCertas || 0) + (s.questoesErradas || 0) + (s.questoesBranco || 0),
      0,
    );
    const totalDisciplines = disciplinas.length;
    const completedReviews = revisoes.filter((r) => r.concluida).length;
    const totalSimulados = simulados.length;
    const bestSimuladoNota = simulados.length > 0 ? Math.max(...simulados.map((s) => s.notaFinal ?? 0)) : 0;

    // XP Calculation
    // 1h study = 100 XP, 1 question = 10 XP, 1 simulado = 500 XP
    const totalXP = Math.round((totalHours * 100) + (totalQuestions * 10) + (totalSimulados * 500));
    const currentLevel = Math.floor(totalXP / 1000) + 1;
    const xpToNextLevel = (currentLevel * 1000) - totalXP;
    const levelProgress = Math.round(((totalXP % 1000) / 1000) * 100);

    return {
      bestStreak,
      totalSessions,
      totalHours,
      totalQuestions,
      totalDisciplines,
      completedReviews,
      totalSimulados,
      bestSimuladoNota,
      totalXP,
      currentLevel,
      xpToNextLevel,
      levelProgress,
    };
  }, [sessoes, disciplinas, revisoes, simulados, diaDescanso]);

  const badges: UnlockedBadge[] = useMemo(() => {
    return BADGES.map((badge) => {
      let currentValue = 0;
      switch (badge.category) {
        case 'streak': currentValue = stats.bestStreak; break;
        case 'sessions': currentValue = stats.totalSessions; break;
        case 'time': currentValue = Math.floor(stats.totalHours); break;
        case 'questions': currentValue = stats.totalQuestions; break;
        case 'disciplines': currentValue = stats.totalDisciplines; break;
        case 'reviews': currentValue = stats.completedReviews; break;
        case 'simulados' as any:
          if (badge.id === 'simulados_elite') {
            currentValue = stats.bestSimuladoNota >= 90 ? 1 : 0;
          } else {
            currentValue = stats.totalSimulados;
          }
          break;
      }
      const unlocked = currentValue >= badge.threshold;
      const progress = Math.min(100, Math.round((currentValue / badge.threshold) * 100));
      return { ...badge, currentValue, unlocked, progress };
    });
  }, [stats]);

  const unlockedCount = badges.filter((b) => b.unlocked).length;
  const totalCount = badges.length;

  return { badges, unlockedCount, totalCount, stats };
}
